import camelCase from 'lodash/camelCase'
import omit from 'lodash/omit'
import { ISchemaBoundValidationContext, ValidationContext } from 'validx'
import { getHttpErrorConstructorForStatusCode } from 'fejl'
import { AxiosError, AxiosResponse } from 'axios'

export function ignore404(err: AxiosError) {
  if (err.response && err.response.status === 404) return null
  throw err
}

export function assertNotNil<T>(
  value: T | null | undefined,
  message: string,
): Exclude<T, null | undefined> {
  if (value === null || value === undefined) {
    throw new Error(message)
  }
  return value as any as Exclude<T, null | undefined>
}

export function extractMessageFromError(err: unknown): string {
  if (err === undefined || err === null || err === false) {
    return ''
  }

  if (typeof err === 'string') {
    return err
  }

  if (isAxiosError(err)) {
    return (
      ((err.response.data
        ? err.response.data.errors && Array.isArray(err.response.data.errors)
          ? 'Validation errors: ' +
            err.response.data.errors
              .map(
                (v: { field: string; message: string }) =>
                  `${v.field} ${v.message}`,
              )
              .join(', ')
          : err.response.data.message
        : err.message) ||
        'A server error occurred, but not sure what it was.') + ''
    )
  }

  if (err instanceof Error) {
    return err.message + ''
  }

  console.error('Unable to extract error message.', err)
  return 'An error occurred, but not sure what it is.'
}

export function rethrowRequestError(err: AxiosError): never {
  if (err.response) {
    const message = extractMessageFromError(err)
    const ErrorClass = getHttpErrorConstructorForStatusCode(err.response.status)
    const data = (err.response?.data as any) || {}
    throw new ErrorClass(message, omit(data, 'message'))
  }
  throw err
}

export function populateValidationContextFromError<T, U extends keyof T>(
  context: ISchemaBoundValidationContext<T> | ValidationContext,
  err: AxiosError,
  modelFieldNameCasing?: 'camel',
) {
  const data: any = err.response?.data
  const errors = data.errors || []
  const mapped = {} as Record<U, string[]>
  errors.forEach((err: any) => {
    const field: U =
      modelFieldNameCasing === 'camel' ? camelCase(err.field) : err.field
    mapped[field] = [...(mapped[field] || []), err.message]
  })
  context.addErrors(mapped)
  return err
}

function isAxiosError(
  err: unknown,
): err is AxiosError<any> & { response: AxiosResponse<any> } {
  return !!(err as any)?.response
}
